<template>
  <v-dialog
    v-model="dialog"
    :fullscreen="isMobile"
    :hide-overlay="isMobile"
    width="800"
    persistent
  >
    <v-card class="serial-dialog-card">
      <v-col class="d-flex justify-between-space serial-dialog-header">
        <p class="font-weight-medium mt-2">
          {{ $t("serial-management") }}
        </p>

        <v-spacer />

        <v-btn icon color="grey darken-2" @click="close">
          <v-icon class="close-hover" large>mdi-close</v-icon>
        </v-btn>
      </v-col>

      <div class="serial-info-row px-4 py-3">
        <div class="serial-info-item">
          <div class="serial-info-label">{{ $t("product-name") }}</div>

          <div class="serial-info-value">{{ productName }}</div>
        </div>

        <div class="serial-info-divider" />

        <div class="serial-info-item">
          <div class="serial-info-label">{{ $t("entrepot-name") }}</div>

          <div class="serial-info-value">{{ entrepotName }}</div>
        </div>

        <div v-if="hasTotalCount" class="serial-info-divider" />

        <div v-if="hasTotalCount" class="serial-info-item">
          <div class="serial-info-label">{{ $t("total-product-count") }}</div>
          <div class="serial-info-value">{{ displayTotalCount }} {{ $t("piece") }}</div>
        </div>
      </div>

      <div v-if="hasTotalCount" class="px-4 pb-3">
        <div class="d-flex align-center mt-1 serial-progress-row">
          <div class="flex-grow-1 mt-3">
            <div class="serial-progress-track" :style="progressTrackStyle" />

            <div class="d-flex justify-space-between align-center mt-2">
              <span v-if="!isOutputMode" class="caption grey--text">
                {{ effectiveSerialCount }} {{ $t("from") }} {{ displayTotalCount }}

                {{ $t("serial-entered") }}
              </span>
              <span v-if="isOutputMode" class="caption grey--text">
                {{ effectiveSerialCount }} {{ $t("from") }} {{ displayTotalCount }}

                {{ $t("serial-selected") }}
              </span>
              <span class="caption font-weight-bold serial-progress-percent">
                {{ serialProgressPercent }}%
              </span>
            </div>
          </div>

          <v-chip
            v-if="remainingCount > 0"
            small
            outlined
            class="serial-remaining-chip ms-3 mb-2"
          >
            {{ remainingCount }} {{ $t("remaining") }}
          </v-chip>
        </div>
      </div>

      <div
        v-else
        class="serial-registered-banner serial-registered-banner--full caption text-center"
      >
        <span v-if="!isOutputMode">
          {{ effectiveSerialCount }} {{ $t("serial-registered") }}</span
        >
        <span v-if="isOutputMode">
          {{ effectiveSerialCount }} {{ $t("serial-selected") }}</span
        >
      </div>

      <v-row v-if="isInputMode && !readonly" class="ma-0 px-4 py-3" align="center">
        <v-col cols="12" sm="9" class="pa-0 pt-2 pe-2 mb-5">
          <v-text-field
            ref="serialInput"
            v-model="serialInput"
            outlined
            dense
            height="41"
            autofocus
            hide-details
            :placeholder="$t('enter-or-scan-serial')"
            @keydown.enter.prevent="addSerial"
          >
            <template v-slot:prepend-inner>
              <v-icon class="serial-barcode-icon">mdi-barcode</v-icon>
            </template>
          </v-text-field>
        </v-col>

        <v-col cols="12" sm="3" class="pa-0 pt-2">
          <v-btn color="#0066ff" dark block height="40" @click="addSerial">
            <v-icon class="close-hover" small>mdi-plus</v-icon> {{ $t("add") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-row class="ma-0 px-4 py-3" align="center">
        <v-col cols="12" sm="12" class="pa-0 pt-1 mb-8">
          <v-text-field
            v-model="search"
            outlined
            dense
            height="41"
            hide-details
            prepend-inner-icon="mdi-magnify"
            :placeholder="$t('search-in-serials')"
            clearable
          />
        </v-col>
      </v-row>

      <div
        class="serial-table-wrapper px-4"
        :class="{
          'serial-table-wrapper--scrollable': isOutputMode || filteredSerials.length > 5,
        }"
      >
        <v-progress-linear
          v-if="loading && !filteredSerials.length"
          indeterminate
          color="primary"
          class="mb-2"
        />
        <v-simple-table dense class="serial-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="">{{ $t("operations") }} #</th>

                <th>{{ $t("serial-number") }}</th>

                <th>{{ $t("register-date") }}</th>

                <th v-if="isInputMode && !readonly">{{ $t("remove") }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(item, index) in filteredSerials" :key="item.uid">
                <td class="serial-row-index">
                  <v-checkbox
                    v-if="isOutputMode && !readonly"
                    v-model="item.selected"
                    hide-details
                    dense
                    class="mt-0 pt-0 serial-row-checkbox"
                    @change="onSelectChange(item)"
                  />
                  <span>{{ index + 1 }}</span>
                </td>

                <td class="serial-value">{{ item.value }}</td>

                <td>{{ item.created_at }}</td>

                <td v-if="isInputMode && !readonly">
                  <v-btn icon small @click="removeSerial(item)">
                    <v-icon small color="pink">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </td>
              </tr>

              <tr v-if="isOutputMode && hasMore && !loading">
                <td :colspan="tableColspan" class="text-center py-2">
                  <div
                    v-observe-visibility="{
                      callback: onSerialListEnd,
                    }"
                    class="serial-load-more-sentinel"
                  />
                </td>
              </tr>

              <tr v-if="isOutputMode && loading && filteredSerials.length">
                <td :colspan="tableColspan" class="text-center py-2">
                  <v-progress-circular
                    :size="20"
                    :width="2"
                    color="primary"
                    indeterminate
                  />
                </td>
              </tr>

              <tr v-if="loading && !filteredSerials.length">
                <td :colspan="tableColspan" class="text-center grey--text py-4">
                  {{ $t("searching") }}
                </td>
              </tr>

              <tr v-else-if="!filteredSerials.length">
                <td :colspan="tableColspan" class="text-center grey--text py-4">
                  {{ $t("no-serial-found") }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <v-divider />

      <v-card-actions class="serial-footer px-4">
        <v-btn v-if="!readonly" color="#0066ff" dark @click="confirm">
          <!-- <v-icon left small>mdi-check</v-icon> -->
          {{ $t("submit") }}
        </v-btn>

        <v-spacer />

        <span class="caption serial-registered-banner serial-registered-footer">
          <span v-if="!isOutputMode">
            {{ effectiveSerialCount }} {{ $t("serial-registered") }}</span
          >
          <span v-if="isOutputMode">
            {{ effectiveSerialCount }} {{ $t("serial-selected") }}</span
          >
        </span>

        <v-spacer />

        <v-btn outlined color="grey darken-1" @click="close">
          {{ readonly ? $t("close") : $t("cancel") }}
        </v-btn>
      </v-card-actions>

      <v-snackbar v-model="duplicateSnackbar" absolute top color="pink" :timeout="1000">
        {{ $t("serial-already-registered") }}
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "SerialManagementDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    product: {
      type: Object,
      default: () => ({}),
    },
    entrepot: {
      type: Object,
      default: () => ({}),
    },
    totalCount: {
      type: [Number, String],
      default: null,
    },
    serials: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasMore: {
      type: Boolean,
      default: false,
    },
    selectedSerials: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    serialFlow: {
      type: String,
      default: "in",
      validator: (value) => ["in", "out"].includes(value),
    },
  },

  data: () => ({
    serialInput: "",
    search: "",
    searchTimer: null,
    localSerials: [],
    selectedById: {},
    selectionState: {},
    duplicateSnackbar: false,
  }),
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    isOutputMode() {
      return this.serialFlow === "out";
    },
    isInputMode() {
      return this.serialFlow === "in";
    },
    tableColspan() {
      return this.isInputMode && !this.readonly ? 4 : 3;
    },
    selectedSerialCount() {
      if (this.isOutputMode) return Object.keys(this.selectedById).length;
      return this.localSerials.filter((item) => item.selected).length;
    },
    effectiveSerialCount() {
      return this.isOutputMode ? this.selectedSerialCount : this.localSerials.length;
    },
    productName() {
      return (this.product && this.product.name) || "-";
    },
    entrepotName() {
      return (this.entrepot && this.entrepot.name) || "-";
    },
    parsedTotalCount() {
      return this.parseCountValue(this.totalCount);
    },
    hasTotalCount() {
      return this.parsedTotalCount !== null;
    },
    displayTotalCount() {
      return this.parsedTotalCount;
    },
    remainingCount() {
      if (!this.hasTotalCount) return 0;
      return Math.max(0, this.parsedTotalCount - this.effectiveSerialCount);
    },
    serialProgressPercent() {
      if (!this.hasTotalCount) return 0;
      const percent = (this.effectiveSerialCount / this.parsedTotalCount) * 100;
      return Math.min(100, Math.max(0, Math.round(percent)));
    },
    progressTrackStyle() {
      const percent = this.serialProgressPercent;
      return {
        background: `linear-gradient(to right, #0066ff ${percent}%, #e0e0e0 ${percent}%)`,
      };
    },
    filteredSerials() {
      if (this.isOutputMode) return this.localSerials;
      const q = (this.search || "").toString().trim();
      if (!q) return this.localSerials;
      return this.localSerials.filter((item) =>
        String(item.value).toLowerCase().includes(q.toLowerCase())
      );
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.selectedById = {};
        this.selectionState = {};
        this.seedSelectedFromProps();
        this.syncLocalSerials();
        this.$nextTick(() => this.focusInput());
      } else {
        this.serialInput = "";
        this.search = "";
        this.duplicateSnackbar = false;
        clearTimeout(this.searchTimer);
      }
    },
    serials: {
      handler() {
        if (this.value) this.syncLocalSerials();
      },
      deep: true,
    },

    search(val) {
      if (!this.value || !this.isOutputMode) return;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.$emit("search", (val || "").toString().trim());
      }, 400);
    },
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },

  methods: {
    parseCountValue(value) {
      if (value === null || value === undefined) return null;
      let str = String(value).trim();
      if (!str) return null;
      const persianDigits = "۰۱۲۳۴۵۶۷۸۹";
      const arabicDigits = "٠١٢٣٤٥٦٧٨٩";
      str = str.replace(/[۰-۹]/g, (char) => String(persianDigits.indexOf(char)));
      str = str.replace(/[٠-٩]/g, (char) => String(arabicDigits.indexOf(char)));
      str = str.replace(/[,،٬\u00a0\s]/g, "");
      const count = Number(str);
      if (!Number.isFinite(count) || count <= 0) return null;
      return Math.floor(count);
    },
    formatSerialDate(date) {
      if (!date) return "";
      const str = String(date);
      if (/^\d{4}\/\d{2}\/\d{2}$/.test(str)) return str;
      if (!this.$moment) return str;
      const m = this.$moment(str);
      return m.isValid() ? m.format("jYYYY/jMM/jDD") : str;
    },
    seedSelectedFromProps() {
      if (!this.isOutputMode) return;
      (this.selectedSerials || []).forEach((item) => {
        const id = item && (item.id || item.serial_id);
        if (id == null) return;
        this.$set(this.selectedById, id, {
          id,
          uid: item.uid || id,
          value: String(item.value || "").trim(),
          created_at: this.formatSerialDate(item.created_at),
          selected: true,
          product_id: item.product_id || null,
        });
        this.$set(this.selectionState, id, true);
      });
    },
    rememberSelection(item) {
      if (!this.isOutputMode || !item || item.id == null) return;
      this.$set(this.selectionState, item.id, !!item.selected);
      if (item.selected) {
        this.$set(this.selectedById, item.id, { ...item, selected: true });
      } else {
        this.$delete(this.selectedById, item.id);
      }
    },
    onSelectChange(item) {
      this.rememberSelection(item);
    },
    onSerialListEnd(isVisible) {
      if (!isVisible || !this.isOutputMode || this.loading || !this.hasMore) return;
      this.$emit("load-more");
    },
    syncLocalSerials() {
      this.localSerials.forEach((item) => this.rememberSelection(item));
      this.localSerials = (this.serials || [])
        .map((item, index) => {
          const id = item.id || null;
          const remembered =
            this.isOutputMode && id != null && this.selectionState[id] !== undefined;
          return {
            id,
            uid: item.uid || item.id || `${item.value}-${index}`,
            value: String(item.value || "").trim(),
            created_at: this.formatSerialDate(item.created_at),
            selected: remembered
              ? !!this.selectionState[id]
              : item.selected !== undefined
              ? !!item.selected
              : !this.isOutputMode,
          };
        })
        .filter((item) => item.value);
      this.localSerials.forEach((item) => this.rememberSelection(item));
    },
    focusInput() {
      if (!this.isInputMode) return;
      const input = this.$refs.serialInput;
      if (input && input.focus) input.focus();
    },
    addSerial() {
      if (this.readonly) return;
      const value = (this.serialInput || "").toString().trim();
      if (!value) return;
      const exists = this.localSerials.some(
        (item) => String(item.value).toLowerCase() === value.toLowerCase()
      );
      if (exists) {
        this.duplicateSnackbar = false;
        this.$nextTick(() => {
          this.duplicateSnackbar = true;
        });
        this.serialInput = "";
        this.$nextTick(() => this.focusInput());
        return;
      }
      this.localSerials.unshift({
        uid: `${Date.now()}-${value}`,
        value,
        created_at: this.$moment ? this.$moment().format("jYYYY/jMM/jDD") : "",
        selected: true,
      });
      this.serialInput = "";
      this.$nextTick(() => this.focusInput());
    },

    removeSerial(item) {
      if (this.readonly) return;
      this.localSerials = this.localSerials.filter((row) => row.uid !== item.uid);
      this.$nextTick(() => this.focusInput());
    },
    close() {
      this.dialog = false;
    },
    confirm() {
      if (this.readonly) {
        this.close();
        return;
      }
      this.localSerials.forEach((item) => this.rememberSelection(item));
      const rows = this.isOutputMode
        ? Object.keys(this.selectedById).map((id) => this.selectedById[id])
        : this.localSerials;
      this.$emit(
        "confirm",
        rows.map((item) => ({
          id: item.id || null,
          product_id: this.product && this.product.id ? this.product.id : null,
          value: String(item.value),
          created_at: item.created_at,
          uid: item.uid,
          selected: true,
        }))
      );
      this.dialog = false;
    },
  },
};
</script>

<style scoped>
.serial-dialog-card {
  position: relative;
  overflow: hidden;
}

.serial-info-row {
  display: flex;

  align-items: stretch;

  background: #f0f3f6;

  gap: 8px;
}

.serial-info-item {
  flex: 1;

  min-width: 0;
}

.serial-info-label {
  font-size: 12px;

  color: #858585;

  margin-bottom: 2px;
}

.serial-info-value {
  font-size: 14px;

  font-weight: 600;

  color: #212121;
}

.serial-info-divider {
  width: 1px;

  background: #e0e0e0;

  margin: 0 8px;
}

.serial-barcode-icon {
  transform: rotate(90deg);

  transform-origin: center center;

  margin-top: 4px;
}

.serial-dialog-header {
  background: transparent !important;
}

.serial-progress-row {
  gap: 12px;
}

.serial-progress-track {
  width: 100%;

  height: 12px;

  border-radius: 6px;

  direction: ltr;
}

.serial-progress-percent {
  color: #0066ff;

  flex-shrink: 0;

  margin-left: 8px;
}

.serial-remaining-chip {
  border-color: #0066ff !important;

  color: #0066ff !important;

  background: #fff8f0 !important;

  flex-shrink: 0;
}

.serial-registered-banner {
  background: #e8f1ff;

  color: #0066ff;

  padding: 8px 12px;

  border-radius: 6px;

  border: 1px solid #a7c7f8;
}

.serial-registered-banner--full {
  width: 100%;

  border-radius: 0;

  padding: 10px 16px;
}

.serial-registered-footer {
  display: inline-block;
}

.serial-table-wrapper {
  width: 100%;
}

.serial-table-wrapper--scrollable ::v-deep .v-data-table__wrapper {
  max-height: 240px;

  overflow-y: auto;
}

.serial-table-wrapper--scrollable ::v-deep thead th {
  position: sticky;

  top: 0;

  z-index: 2;
}

.serial-table thead th {
  background: #bab9b9;

  color: #fff !important;
}

.serial-value {
  direction: ltr;
  text-align: right;
  font-family: monospace;
  border-left: 1px solid #e0e0e0;
}

.serial-footer {
  background: #f0f3f6;
}

.serial-row-index {
  white-space: nowrap;
  width: 1%;
  border-left: 1px solid #e0e0e0;
  padding-left: 12px;
}
.serial-row-index .serial-row-checkbox {
  display: inline-flex;
  width: auto;
  margin: 0 4px 0 0 !important;
  padding-top: 0 !important;
  vertical-align: middle;
}
.serial-row-checkbox ::v-deep .v-input__control,
.serial-row-checkbox ::v-deep .v-input__slot {
  min-width: 0 !important;
  width: auto !important;
}

.serial-load-more-sentinel {
  min-height: 12px;
  width: 100%;
}
</style>
